<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { arrow_right, arrow_left } from "pn-design-assets/pn-assets/icons";
import Header from "@/components/reusables/CommonHeader.vue";
import CreateCollectionStep2 from "@/components/stickers/CreateCollectionStep2.vue";
import CreateCollectionStep3 from "@/components/stickers/CreateCollectionStep3.vue";
import CreateCollectionStep4 from "@/components/stickers/CreateCollectionStep4.vue";
import CreateCollectionStep5 from "@/components/stickers/CreateCollectionStep5.vue";
import StickerButtons from "@/components/stickers/StickerButtons.vue";
import { storeToRefs } from "pinia";
import { useImageStore } from "@/stores/images";
import { useRouter, useRoute } from "vue-router";
import { userStickers } from "@/stores/stickers";
import { useTagManagementStore } from "@/stores/tagManagement";

const heading = ref("Edit Collection");
const goBackHref = ref("/stickers");
const goBackText = ref("Back to sticker collection");
const description = ref(
  "Follow the four steps to edit the collection details.",
);
const router = useRouter();
const route = useRoute();
const currentStep = ref(1);
const totalSteps = ref(4);

const stickerStore = userStickers();
const { createCollectionReqestParams, notificationObj } =
  storeToRefs(stickerStore);
const imageStore = useImageStore();
const tagManagementStore = useTagManagementStore();

const showSpinner = ref(false);
const step3Ref = ref<InstanceType<typeof CreateCollectionStep3>>();
const step4Ref = ref<InstanceType<typeof CreateCollectionStep4>>();
const step5Ref = ref<InstanceType<typeof CreateCollectionStep5>>();
const editCollectionId = computed(() => Number(route.params.id));

onMounted(async () => {
  try {
    showSpinner.value = true;
    stickerStore.reset();

    if ((imageStore.categories?.length ?? 0) === 0) {
      await imageStore.getImageCategories();
    }

    if (!tagManagementStore.tagTree) {
      await tagManagementStore.getTagTree();
    }

    await stickerStore.getStickerCollectionById(editCollectionId.value);
    stickerStore.populateCreateCollectionRequestFromCollection(
      stickerStore.currentCollection,
    );
  } catch {
    stickerStore.populateNotificationObj({
      show: true,
      message: "There was an error in fetching collection details",
      status: "error",
    });
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
  } finally {
    showSpinner.value = false;
  }
});

const goToNextStep = async () => {
  stickerStore.populateNotificationObj({
    show: false,
    message: "",
    status: "",
  });

  switch (currentStep.value) {
    case 1:
      stickerStore.createCollectionProgress.step1 = true;
      if (
        createCollectionReqestParams.value.svName !== "" &&
        createCollectionReqestParams.value.enName !== ""
      ) {
        currentStep.value++;
      }
      break;
    case 2:
      stickerStore.createCollectionProgress.step2 = true;
      if (step3Ref.value?.validate()) {
        currentStep.value++;
      }
      break;
    case 3:
      stickerStore.createCollectionProgress.step3 = true;
      if (step4Ref.value?.validate()) {
        currentStep.value++;
      }
      break;
    case 4:
      stickerStore.createCollectionProgress.step4 = true;
      if (!step5Ref.value?.validate()) {
        break;
      }
      try {
        showSpinner.value = true;
        await stickerStore.updateStickerCollection();
        stickerStore.populateNotificationObj({
          show: true,
          message: "Collection updated successfully",
          status: "success",
        });
        window.scrollTo(0, 0);
        setTimeout(() => {
          stickerStore.resetNotification();
        }, 4000);
        router.push(`/sticker-collection/${editCollectionId.value}`);
      } catch {
        stickerStore.populateNotificationObj({
          show: true,
          message: "There was an error in updating collection",
          status: "error",
        });
        setTimeout(() => {
          stickerStore.resetNotification();
        }, 4000);
      } finally {
        showSpinner.value = false;
      }
      break;
    default:
      break;
  }
};

const secondaryButtonClicked = () => {
  stickerStore.populateNotificationObj({
    show: false,
    message: "",
    status: "",
  });

  if (currentStep.value === 1) {
    router.push(`/sticker-collection/${editCollectionId.value}`);
  } else {
    currentStep.value--;
  }
};

const primaryButtonText = computed(() => {
  if (currentStep.value === 4) {
    return "Save Collection";
  }
  return "Next";
});

const secondaryButtonText = computed(() => {
  if (currentStep.value === 1) {
    return "Cancel";
  }
  return "Previous";
});
</script>

<template>
  <pn-toast
    class="bottom-toast"
    :appearance="notificationObj.status"
    v-show="notificationObj.show"
    :text="notificationObj.message"
    temporary
  ></pn-toast>
  <Header
    :heading="heading"
    :description="description"
    :go-back-href="goBackHref"
    :go-back-text="goBackText"
    :show-stepper="true"
    :total-steps="totalSteps"
    :current-step="currentStep"
  ></Header>
  <div :class="[{ spinner: showSpinner }]">
    <pn-spinner size="3" v-show="showSpinner" />
  </div>

  <CreateCollectionStep2 v-if="currentStep == 1" />
  <CreateCollectionStep3 v-if="currentStep == 2" ref="step3Ref" />
  <CreateCollectionStep4 v-if="currentStep == 3" ref="step4Ref" />
  <CreateCollectionStep5 v-if="currentStep == 4" ref="step5Ref" />
  <StickerButtons
    :primary-button-text="primaryButtonText"
    :secondary-button-text="secondaryButtonText"
    :primary-icon="arrow_right"
    @click-primary="goToNextStep"
    @click-secondary="secondaryButtonClicked"
    :secondary-icon="currentStep !== 1 ? arrow_left : undefined"
  ></StickerButtons>
</template>

<style lang="scss" scoped>
.name-desc {
  display: flex;
  flex-direction: column;
  padding-left: 32px;
}

.width-67 {
  width: 67%;
}

.gap-32 {
  gap: 32px;
}

.buttons {
  display: flex;
  padding-top: 24px;
  margin-left: 32px;
  gap: 8px;
  justify-content: flex-start;
}

.bottom-toast {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: fit-content;
  max-width: calc(100vw - 32px);
  text-align: center;
  z-index: 1000;
}
</style>
